<template>
    <div>
        <v-row no-gutters class="pt-1">
            <v-col cols="5">
                <v-text-field-filled
                    v-model="learningModule.name"
                    label="Nom"
                    outlined
                    class="pr-1"
                    density="compact"
                    hide-details
                />
            </v-col>
            <v-col cols="3">
                <v-select-filled
                    v-model="learningModule.status"
                    label="Statut"
                    class="pr-1"
                    :items="statusOptions"
                    item-text="text"
                    item-value="value"
                    :item-title="(item) => item.text"
                    density="compact"
                    outlined
                />
            </v-col>
            <v-col cols="2">
                <v-text-field-filled
                    v-model.number="learningModule.difficulty"
                    label="Niveau de difficulté"
                    outlined
                    class="pr-1"
                    density="compact"
                    hide-details
                />
            </v-col>
            <v-col cols="2">
                <v-select-filled
                    v-model="learningModule.profileType"
                    label="Type de profil"
                    :items="profileTypes"
                    density="compact"
                    outlined
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-text-field-filled
                    v-model="learningModule.description"
                    label="Description"
                    outlined
                    density="compact"
                    hide-details
                />
            </v-col>
        </v-row>

        <!-- Nouvelle méthode de sélection des objets d'apprentissage -->
        <v-row>
            <v-col cols="11">
                <v-select-filled
                    v-model="selectedLearningObject"
                    :items="availableLearningObjects"
                    item-title="name"
                    item-value="id"
                    label="Sélectionner un objet d'apprentissage"
                    density="compact"
                    outlined
                    clearable
                />
            </v-col>
            <v-col cols="1">
                <v-btn-icon
                    color="primary"
                    dense
                    icon="$add"
                    rounded="xl"
                    class="d-flex align-center justify-center elevation-0"
                    @click="addLearningObject"
                />
            </v-col>
        </v-row>

        <!-- Liste des objets d'apprentissage sélectionnés avec possibilité de supprimer -->
        <v-row>
            <v-col cols="12">
                <v-list>
                    <v-list-item
                        v-for="(learningObject, index) in learningModule.learningObjects"
                        :key="index"
                        class="mb-2"
                        rounded="xl"
                        lines="two"
                        border
                    >
                        <v-list-item-icon>
                            <v-icon>$fa-photo-video</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>{{ learningObject.name }}</v-list-item-title>
                        <template #append>
                            <v-btn-icon icon="$fa-minus" @click="removeLearningObject(index)">
                            </v-btn-icon>
                        </template>
                    </v-list-item>
                </v-list>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-text-field-filled
                    v-model="learningModule.key"
                    label="Clé unique ( vide pour générer automatiquement )"
                    outlined
                    density="compact"
                    hide-details
                    :readonly="!!learningModuleId"
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" class="d-flex justify-end">
                <v-btn
                    class="ml-2 mr-0 elevation-0"
                    color="primary"
                    dark
                    rounded
                    dense
                    outlined
                    @click="deleteLearningModule"
                    v-if="learningModuleId"
                >
                    Supprimer
                </v-btn>
                <v-btn
                    class="ml-2 mr-0 elevation-0"
                    color="primary"
                    dark
                    rounded
                    dense
                    :disabled="isSaveDisabled"
                    @click="upsertTutorialsModule"
                >
                    Valider
                </v-btn>
            </v-col>
        </v-row>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, toNative } from "vue-facing-decorator";
import LEARNING_MODULE from "../../graphql/tutorials/TutorialsModule.gql";
import UPSERT_TUTORIALS_MODULE from "../../graphql/tutorials/UpsertTutorialsModule.gql";
import DELETE_LEARNING_MODULE from "../../graphql/tutorials/DeleteTutorialsModule.gql";
import LEARNING_OBJECTS from "../../graphql/tutorials/GetAllTutorials.gql";
import { Apollo } from "../../decorators";

@Component({
    emits: ["refresh"],
})
export class TutorialsModuleEdition extends Vue {
    @Prop({ default: null }) learningModuleId!: string | null;

    statusOptions = [
        { text: "Brouillon", value: "DRAFT" },
        { text: "Publié", value: "PUBLISHED" },
    ];

    profileTypes = ["ROOT", "ACCOUNTING", "ACCOUNTING_CLIENT", "BUSINESS"];

    selectedLearningObject: any = null; // Nouveau tutoriel sélectionné

    // Requête pour récupérer les objets d'apprentissage disponibles
    @Apollo({
        query() {
            return LEARNING_OBJECTS;
        },
        variables() {
            return {};
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data?.learningObjects) {
                    this.availableLearningObjects = data.learningObjects;
                } else {
                    this.availableLearningObjects = [];
                }
            }
        },
    })
    availableLearningObjects: any = [];

    // Requête pour récupérer le module d'apprentissage
    @Apollo({
        query() {
            return LEARNING_MODULE;
        },
        variables() {
            return {
                id: this.learningModuleId,
            };
        },
        skip() {
            const shouldSkip = !this.learningModuleId;
            if (shouldSkip) {
                this.resetLearningModule();
            }
            return shouldSkip;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data?.tutorialsModule?.tutorialsModule) {
                    this.learningModule = {
                        ...data.tutorialsModule.tutorialsModule,
                    };
                } else {
                    this.resetLearningModule();
                }
            }
        },
    })
    learningModule: any = {
        id: "",
        name: "",
        description: "",
        difficulty: 0,
        status: "",
        profileType: "",
        learningObjects: [] as any[], // Liste des objets d'apprentissage sélectionnés
        key: "",
    };

    // Fonction pour ajouter un objet d'apprentissage à la liste
    addLearningObject() {
        const alreadyExists = this.learningModule.learningObjects.some(
            (obj: any) => obj.id === this.selectedLearningObject,
        );
        if (!alreadyExists && this.selectedLearningObject) {
            debugger;

            const obj = {
                id:
                    this.availableLearningObjects.find(
                        (obj: any) => obj.id === this.selectedLearningObject,
                    ).id || "",
                name:
                    this.availableLearningObjects.find(
                        (obj: any) => obj.id === this.selectedLearningObject,
                    ).name || "",
            };

            this.learningModule.learningObjects.push(obj);
            this.selectedLearningObject = null; // Réinitialiser la sélection
        }
    }

    // Fonction pour retirer un objet d'apprentissage de la liste
    removeLearningObject(index: number) {
        this.learningModule.learningObjects.splice(index, 1);
    }

    // Méthode pour insérer ou mettre à jour le module d'apprentissage
    async upsertTutorialsModule() {
        const variables = {
            ...this.learningModule,
            id: this.learningModuleId || "",
            learningObjects: this.learningModule.learningObjects.map((obj: any) => ({
                id: obj.id, // Utiliser uniquement l'ID ou un autre champ unique
            })),
            key: this.learningModule?.key ? this.learningModule.key : undefined,
        };

        try {
            await this.$apollo.mutate({
                mutation: UPSERT_TUTORIALS_MODULE,
                variables,
            });
            this.$emit("refresh");
        } catch (error) {
            console.error("Failed to save learning module:", error);
        }
    }

    async deleteLearningModule() {
        if (!this.learningModuleId) return;

        try {
            await this.$apollo.mutate({
                mutation: DELETE_LEARNING_MODULE,
                variables: { id: this.learningModuleId },
            });
            this.$emit("refresh");
        } catch (error) {
            console.error("Failed to delete learning module:", error);
        }
    }

    // Désactiver le bouton de validation si les conditions ne sont pas remplies
    get isSaveDisabled() {
        return (
            !this.learningModule.name ||
            !this.learningModule.status ||
            !this.learningModule.profileType ||
            !this.learningModule.learningObjects.length
        );
    }

    // Réinitialiser le module d'apprentissage
    resetLearningModule() {
        this.learningModule = {
            id: "",
            name: "",
            description: "",
            difficulty: 0,
            status: "",
            profileType: "",
            learningObjects: [] as any[],
            key: "",
        };
    }
}

export default toNative(TutorialsModuleEdition);
</script>

<style scoped></style>
